import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";

const FAQS = [
  { q: "What is Travel-Tek?", a: "Travel-Tek is a white-label, multi-tenant commerce platform for destination businesses — manage your destination supply, build packages, set pricing, capture enquiries, create quotations and distribute through your own channels." },
  { q: "Is Travel-Tek a website builder?", a: "No. The branded website is one distribution channel. Underneath it, Travel-Tek runs your catalogue, pricing, packages, enquiries, quotations and B2B distribution." },
  { q: "Can I use my own domain?", a: "Yes. Your storefront runs on your own domain, configured and managed for you." },
  { q: "Can I use my own branding?", a: "Yes. Travel-Tek is white-label — your logo, colours, typography and content across every customer touchpoint." },
  { q: "Can I manage multiple destinations?", a: "Yes. Start with one destination and expand across countries — the catalogue, pricing and packages scale with you." },
  { q: "Can I manage hotels, activities and transfers?", a: "Yes. All three are first-class products in your catalogue, with rates, seasons and policies." },
  { q: "Can I create packages?", a: "Yes. Build day-by-day itineraries from your own inventory — hotels, activities, transfers, meals and add-ons." },
  { q: "Can I generate quotations?", a: "Yes. Turn any enquiry into an itemized, branded quotation and send it from the same workspace." },
  { q: "Can I manage enquiries?", a: "Yes. Direct, B2B and agent enquiries land in one workspace with statuses, assignment and conversation history." },
  { q: "Can Travel-Tek support B2B travel agents?", a: "Yes. Agents get a digital channel to discover your products, check information and request quotations. Direct agent booking is a roadmap phase." },
  { q: "Is the platform multi-tenant?", a: "Yes. Each business operates in its own branded environment with its own domain, data, partners and customers." },
  { q: "Can the platform support multiple currencies?", a: "Yes — pricing and quotations support the currencies destination businesses sell in, including INR, AED, USD, THB, VND and SGD." },
  { q: "Can I integrate APIs later?", a: "Yes. The architecture is API-ready; hotel, activity and distribution APIs are planned expansion phases." },
  { q: "Can I start with one destination?", a: "Yes — that's the recommended path. Launch one destination well, then scale your catalogue across markets." },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 lg:py-32">
      <div className="container-x grid grid-cols-1 gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Reveal><Chapter index={14} label="FAQ" /></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-8 font-serif text-3xl leading-[1.15] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Questions destination businesses ask us.
            </h2>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-stone2">
              Straight answers about the platform, branding, distribution and scale.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.12} className="lg:col-span-8">
          <div className="divide-y divide-sand border-y border-sand" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <div key={f.q} data-testid={`faq-accordion-item-${i + 1}`}>
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  data-testid={`faq-question-${i + 1}`}
                  className="flex w-full items-center justify-between gap-6 py-5 text-left"
                >
                  <span className={`text-base font-semibold transition-colors duration-300 sm:text-lg ${open === i ? "text-terra" : "text-ink"}`}>
                    {f.q}
                  </span>
                  <Plus className={`h-5 w-5 shrink-0 text-stone2 transition-transform duration-500 ${open === i ? "rotate-45 text-terra" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="max-w-2xl pb-6 text-base leading-relaxed text-stone2">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
